import PropTypes from 'prop-types';
import React from 'react';
import { format } from 'date-fns';
import classNames from 'classnames';
import Sticky from 'react-stickynode';
import { ErrorBox, OptimalScoreLegend } from '@surfline/quiver-react';
import ForecastTableControls from '../ForecastTableControls';
import ForecastTableCTA from '../ForecastTableCTA';
import SwellHeightLegend from '../SwellHeightLegend';
import { getLocalDateFromDayNumber } from '../../utils/getLocalDate';
import waveChartPropType from '../../propTypes/waveChart';
import windChartPropType from '../../propTypes/windChart';
import highlightTypePropType from '../../propTypes/forecastTableHighlightType';
import ForecastTableDay from './ForecastTableDay';
import config from '../../config';

const className = (filterTop3Swells, hideSurfAndWind) =>
  classNames({
    'sl-forecast-table': true,
    'sl-forecast-table--top-3-swells': filterTop3Swells,
    'sl-forecast-table--hide-surf-and-wind': hideSurfAndWind,
  });

const getColumns = ({ hideSurfAndWind, filterTop3Swells }) => {
  const columns = [{ display: 'Day', type: 'day' }];

  if (!hideSurfAndWind) {
    columns.push.apply(columns, [
      { display: 'Surf', type: 'surf' },
      { display: 'Wind', type: 'wind' },
    ]);
  }

  columns.push.apply(columns, [
    { display: 'Swell 1', type: 'swell' },
    { display: 'Swell 2', type: 'swell' },
    { display: 'Swell 3', type: 'swell' },
  ]);

  if (!filterTop3Swells) {
    columns.push.apply(columns, [
      { display: 'Swell 4', type: 'swell' },
      { display: 'Swell 5', type: 'swell' },
      { display: 'Swell 6', type: 'swell' },
    ]);
  }

  return columns;
};

const ForecastTable = ({
  days,
  wave,
  wind,
  utcOffset,
  showCTA,
  category,
  pageName,
  spotId,
  subregionId,
  toggleDayExpanded,
  expandAllDays,
  hideSurfAndWind,
  filterTop3Swells,
  toggleFilterTop3Swells,
  highlightType,
  deviceWidth,
}) => {
  const columns = getColumns({ hideSurfAndWind, filterTop3Swells });
  const mobileView = deviceWidth < config.tabletLargeWidth;
  const dayDateFormat = mobileView ? 'iii' : 'iiii';
  return (
    <div className={className(filterTop3Swells, hideSurfAndWind)}>
      <div className="sl-forecast-table__subheader">
        <ForecastTableControls
          expandAllDays={expandAllDays}
          filterTop3Swells={filterTop3Swells}
          toggleFilterTop3Swells={toggleFilterTop3Swells}
        />
        {highlightType === 'OPTIMAL' ? <OptimalScoreLegend /> : null}
        {highlightType === 'SWELL_HEIGHT' ? (
          <SwellHeightLegend units={wave.units.waveHeight} />
        ) : null}
      </div>
      <div className="sl-forecast-table__container">
        <table className="sl-forecast-table__table">
          <thead>
            <tr>
              {columns.map(({ display, type }) => (
                <th
                  key={display}
                  className={`sl-forecast-table__cell sl-forecast-table__cell--header sl-forecast-table__cell--${type}`}
                  scope="col"
                >
                  {mobileView ? display : <Sticky>{display}</Sticky>}
                </th>
              ))}
            </tr>
          </thead>
          {wave.error || wind.error ? (
            <tbody className="sl-forecast-table__error">
              <tr>
                <th colSpan={columns.length}>
                  <ErrorBox type="data" />
                </th>
              </tr>
            </tbody>
          ) : (
            days.map(({ blurred, expanded }, i) => {
              const day = format(getLocalDateFromDayNumber(i, utcOffset), `${dayDateFormat} M/d`);
              return (
                <ForecastTableDay
                  key={day}
                  units={{
                    waveHeight: wave.units.waveHeight,
                    windSpeed: wind.units.windSpeed,
                  }}
                  day={day}
                  waveDay={wave.days[i]}
                  windDay={wind.days[i]}
                  blurred={blurred}
                  expanded={expanded}
                  hideSurfAndWind={hideSurfAndWind}
                  filterTop3Swells={filterTop3Swells}
                  onClickDay={() => toggleDayExpanded(i)}
                  highlightType={highlightType}
                />
              );
            })
          )}
        </table>
      </div>
      {showCTA ? (
        <ForecastTableCTA
          category={category}
          pageName={pageName}
          spotId={spotId}
          subregionId={subregionId}
        />
      ) : null}
    </div>
  );
};

ForecastTable.propTypes = {
  days: PropTypes.arrayOf(
    PropTypes.shape({
      blurred: PropTypes.bool,
      expanded: PropTypes.bool,
    })
  ).isRequired,
  wave: waveChartPropType.isRequired,
  wind: windChartPropType.isRequired,
  utcOffset: PropTypes.number.isRequired,
  category: PropTypes.string.isRequired,
  pageName: PropTypes.string.isRequired,
  spotId: PropTypes.string.isRequired,
  subregionId: PropTypes.string,
  showCTA: PropTypes.bool,
  toggleDayExpanded: PropTypes.func.isRequired,
  expandAllDays: PropTypes.func.isRequired,
  hideSurfAndWind: PropTypes.bool,
  filterTop3Swells: PropTypes.bool,
  toggleFilterTop3Swells: PropTypes.func.isRequired,
  highlightType: highlightTypePropType.isRequired,
  deviceWidth: PropTypes.number.isRequired,
};

ForecastTable.defaultProps = {
  subregionId: null,
  showCTA: false,
  hideSurfAndWind: false,
  filterTop3Swells: false,
};

export default ForecastTable;
